import * as THREE from "three";
import {
  getCatalogItem,
  getFootprint,
  placementBounds,
  placementRotationY,
  placementScale,
  type ItemPlacement,
  type Plot
} from "@tribe/domain";
import { VOXEL_SIZE } from "./VoxelInstanceBuffer";
import { resolveVoxelAsset } from "./assets/catalog/itemAssets";
import { buildVoxelMeshGroup, disposeVoxelMeshGroup } from "./voxelGroup";

/**
 * Everything a member has put in their yard.
 *
 * Kept out of the static instanced buffers on purpose. Those are rebuilt wholesale — terrain,
 * villagers, particles and all — so routing placements through them would mean reconstructing the
 * village every time somebody nudged a lawn chair. Here each item is its own group, and moving,
 * turning or resizing one is a matrix write.
 *
 * The layer group carries the plot's own transform, so a placement's stored position is literally
 * the plot-local voxel position the domain validates, converted in exactly one place. The group
 * itself stays at world scale, because the art inside it is built in world units already.
 */

type Entry = {
  group: THREE.Group;
  placement: ItemPlacement;
  /** What the group was built from, so a variant change can be told apart from a move. */
  modelKey: string;
  /** The bottom of the art in its own units, so the item can be stood on the ground at any size. */
  artBaseY: number;
};

/** How low the art reaches, in its own voxel units. */
function artBase(voxels: { y: number; scale?: number; scaleY?: number }[]): number {
  let lowest = 0;
  for (const voxel of voxels) lowest = Math.min(lowest, voxel.y - (voxel.scaleY ?? voxel.scale ?? 1) / 2);
  return lowest;
}

function modelKeyFor(placement: ItemPlacement): string {
  return `${placement.itemId}|${JSON.stringify(placement.selectedVariants)}`;
}

/** Which side of the walls a layer draws, and where its frame of reference sits. */
export type LayerTarget =
  | { kind: "yard"; plot: Plot; surfaceY: number }
  /** Inside the home: the frame is the middle of its floor, turned with the building. */
  | { kind: "interior"; origin: { x: number; y: number; z: number }; rotationY: number };

export class PlacedItemsLayer {
  /** The plot's frame of reference: everything inside is in plot-local voxels. */
  private readonly root = new THREE.Group();
  private readonly entries = new Map<string, Entry>();
  /** A ring on the ground under whatever is being arranged. */
  private readonly marker: THREE.Mesh;
  private selectedId: string | null = null;

  /**
   * Which placements this layer draws.
   *
   * A property's placements are one list covering both sides of the walls, and there is a layer
   * for each — the yard's and the room's — so the filter is what keeps a bedside table out of the
   * garden. `sync` removes anything it does not see, so an unfiltered layer would delete the
   * other's items on every update.
   */
  private readonly shows: { surface: "exterior" | "interior" };

  constructor(scene: THREE.Scene, target: LayerTarget) {
    if (target.kind === "yard") {
      // `surfaceY` is the top face of the levelled pad, in voxels — what items stand on.
      this.shows = { surface: "exterior" };
      this.root.position.set(
        target.plot.position.x * VOXEL_SIZE,
        target.surfaceY * VOXEL_SIZE,
        target.plot.position.z * VOXEL_SIZE
      );
      this.root.rotation.y = -target.plot.rotationY;
    } else {
      // The home's floor, turned the way the building is, so a stored position is home-local
      // voxels with nothing between it and the mesh but `VOXEL_SIZE` — exactly what the domain
      // validates against.
      this.shows = { surface: "interior" };
      this.root.position.set(
        target.origin.x * VOXEL_SIZE,
        target.origin.y * VOXEL_SIZE,
        target.origin.z * VOXEL_SIZE
      );
      this.root.rotation.y = -target.rotationY;
    }
    scene.add(this.root);

    // Lies flat just above the grass, so it reads as a shadow on the ground rather than a
    // floating shape. Sized per selection.
    this.marker = new THREE.Mesh(
      new THREE.RingGeometry(0.82, 1, 40),
      new THREE.MeshBasicMaterial({ color: 0xf6eedc, transparent: true, opacity: 0.85, depthWrite: false })
    );
    this.marker.rotation.x = -Math.PI / 2;
    this.marker.visible = false;
    this.marker.renderOrder = 2;
    this.root.add(this.marker);
  }

  /** Rings whichever item is being arranged, or clears the ring when nothing is. */
  public setSelected(placementId: string | null): void {
    this.selectedId = placementId;
    this.refreshMarker();
  }

  private refreshMarker(): void {
    const entry = this.selectedId ? this.entries.get(this.selectedId) : undefined;
    if (!entry) {
      this.marker.visible = false;
      return;
    }
    const bounds = placementBounds(
      getFootprint(entry.placement.itemId),
      entry.placement.rotationQuarters,
      entry.placement.scaleStep
    );
    // Wide enough to sit outside the item's own footprint at any angle.
    const radius = Math.hypot(bounds.halfX, bounds.halfZ) + 0.35;
    this.marker.scale.setScalar(radius * VOXEL_SIZE);
    this.marker.position.set(
      (entry.placement.position.x + bounds.offsetX) * VOXEL_SIZE,
      // Under the item rather than on the floor, so the ring follows a plant up onto a table
      // instead of being left behind on the rug.
      entry.placement.position.y * VOXEL_SIZE + 0.06,
      (entry.placement.position.z + bounds.offsetZ) * VOXEL_SIZE
    );
    this.marker.visible = true;
  }

  /** The meshes a raycaster should test against. */
  public get pickables(): THREE.Object3D[] {
    return [...this.entries.values()].map((entry) => entry.group);
  }

  public groupFor(placementId: string): THREE.Group | undefined {
    return this.entries.get(placementId)?.group;
  }

  /** Where the plot's own frame sits, for turning a pointer hit into a plot-local position. */
  public get frame(): THREE.Group {
    return this.root;
  }

  /**
   * Where to hang the grips for an item: about head height on it, and how far out they need to
   * sit to clear it.
   */
  public anchorFor(placementId: string): { position: THREE.Vector3; radius: number } | null {
    const entry = this.entries.get(placementId);
    if (!entry) return null;

    const footprint = getFootprint(entry.placement.itemId);
    const bounds = placementBounds(
      footprint, entry.placement.rotationQuarters, entry.placement.scaleStep
    );
    const scale = placementScale(entry.placement.scaleStep);
    const position = entry.group.getWorldPosition(new THREE.Vector3());
    position.y += (footprint.height * scale * VOXEL_SIZE) / 2;

    return {
      position,
      radius: Math.max(Math.hypot(bounds.halfX, bounds.halfZ), footprint.height * scale / 2) * VOXEL_SIZE
    };
  }

  /**
   * Brings the scene in line with the given placements.
   *
   * Diffed rather than rebuilt: an item that only moved keeps its geometry, and only a change of
   * item or variant costs a new model.
   */
  public sync(placements: ItemPlacement[]): void {
    const seen = new Set<string>();

    for (const placement of placements) {
      if (placement.surface !== this.shows.surface) continue;
      seen.add(placement.id);

      const existing = this.entries.get(placement.id);
      const modelKey = modelKeyFor(placement);

      if (existing && existing.modelKey === modelKey) {
        this.applyTransform(existing.group, placement, existing.artBaseY);
        existing.placement = placement;
        continue;
      }

      if (existing) this.remove(placement.id);
      const built = this.build(placement);
      if (!built) continue;
      this.applyTransform(built.group, placement, built.artBaseY);
      this.root.add(built.group);
      this.entries.set(placement.id, { group: built.group, placement, modelKey, artBaseY: built.artBaseY });
    }

    for (const id of [...this.entries.keys()]) {
      if (!seen.has(id)) this.remove(id);
    }

    this.refreshMarker();
  }

  /**
   * Moves an item without going through the store, for the frames during a drag.
   *
   * `y` is optional because outdoors there is only ever the ground to stand on. Indoors it is the
   * height of whatever the item is currently over, so dragging a plant across a table lifts it as
   * it goes rather than showing it at floor height until the drag ends.
   */
  public previewPosition(placementId: string, position: { x: number; y?: number; z: number }): void {
    const entry = this.entries.get(placementId);
    if (!entry) return;
    const scale = placementScale(entry.placement.scaleStep);
    const y = position.y === undefined
      ? entry.group.position.y
      : (position.y - entry.artBaseY * scale) * VOXEL_SIZE;
    entry.group.position.set(position.x * VOXEL_SIZE, y, position.z * VOXEL_SIZE);
    if (this.selectedId === placementId) {
      const bounds = placementBounds(
        getFootprint(entry.placement.itemId), entry.placement.rotationQuarters, entry.placement.scaleStep
      );
      this.marker.position.set(
        (position.x + bounds.offsetX) * VOXEL_SIZE, 0.06, (position.z + bounds.offsetZ) * VOXEL_SIZE
      );
    }
  }

  private build(placement: ItemPlacement): { group: THREE.Group; artBaseY: number } | null {
    let item;
    try {
      item = getCatalogItem(placement.itemId);
    } catch {
      // An item that has left the catalogue should not take the whole village down with it.
      return null;
    }
    const asset = resolveVoxelAsset(item, placement.selectedVariants);
    if (!asset) return null;

    const group = buildVoxelMeshGroup(asset.voxels);
    group.userData.placementId = placement.id;
    return { group, artBaseY: artBase(asset.voxels) };
  }

  /** The one place plot-local voxels become world units. */
  private applyTransform(group: THREE.Group, placement: ItemPlacement, artBaseY: number): void {
    const scale = placementScale(placement.scaleStep);
    group.scale.setScalar(scale);
    group.rotation.y = -placementRotationY(placement.rotationQuarters);
    // A group scales about its own origin, which would lift the item off the ground as it shrank
    // and bury it as it grew. Offsetting by the scaled depth of the art keeps the bottom of the
    // model on the surface at every size.
    group.position.set(
      placement.position.x * VOXEL_SIZE,
      (placement.position.y - artBaseY * scale) * VOXEL_SIZE,
      placement.position.z * VOXEL_SIZE
    );
  }

  private remove(placementId: string): void {
    const entry = this.entries.get(placementId);
    if (!entry) return;
    this.root.remove(entry.group);
    disposeVoxelMeshGroup(entry.group);
    this.entries.delete(placementId);
  }

  public destroy(scene: THREE.Scene): void {
    for (const id of [...this.entries.keys()]) this.remove(id);
    this.marker.geometry.dispose();
    (this.marker.material as THREE.Material).dispose();
    scene.remove(this.root);
  }
}
